import { createContext, useContext, useReducer, useCallback, ReactNode } from 'react'
import { DreamityCoachResDto } from '../types/coach.types'

interface CoachState {
  coaches: DreamityCoachResDto[]
  selectedCoach: DreamityCoachResDto | null
  loading: boolean
  error: string | null
}

type CoachAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_COACHES'; payload: DreamityCoachResDto[] }
  | { type: 'SET_SELECTED_COACH'; payload: DreamityCoachResDto | null }
  | { type: 'DELETE_COACH'; payload: number }

interface CoachContextType extends CoachState {
  // 코치 전체 조회 (dreamId 기반)
  fetchCoaches: (dreamId: string) => Promise<void>
  deleteCoach: (coachId: number, dreamId: string) => Promise<void>
  // 스케줄러용 코치 생성 호출
  generateCoach: () => Promise<void>
  // 코치 선택
  selectCoach: (coach: DreamityCoachResDto | null) => void
}

const CoachContext = createContext<CoachContextType | undefined>(undefined)

const initialState: CoachState = {
  coaches: [],
  selectedCoach: null,
  loading: false,
  error: null,
}

function coachReducer(state: CoachState, action: CoachAction): CoachState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_COACHES':
      return { ...state, coaches: action.payload }
    case 'SET_SELECTED_COACH':
      return { ...state, selectedCoach: action.payload }
    case 'DELETE_COACH':
      return {
        ...state,
        coaches: state.coaches.filter(
          (coach) => coach.coachId !== action.payload
        ),
        // 삭제된 코치가 선택된 코치이면 선택 해제
        selectedCoach:
          state.selectedCoach?.coachId === action.payload
            ? null
            : state.selectedCoach,
      }
    default:
      return state
  }
}

interface CoachProviderProps {
  children: ReactNode
}

export function CoachProvider({ children }: CoachProviderProps) {
  const [state, dispatch] = useReducer(coachReducer, initialState)

  /**
   * 1. 코치 전체 조회
   * 특정 꿈에 대한 코치 전체 조회
   * 백엔드에서 @AuthenticationPrincipal로 userId를 자동으로 가져옴
   */
  const fetchCoaches = useCallback(async (_dreamId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    dispatch({ type: 'SET_COACHES', payload: [] })
    dispatch({ type: 'SET_LOADING', payload: false })
  }, [])

  /**
   * 2. 코치 삭제
   * 특정 코치 기록 삭제
   */
  const deleteCoach = async (coachId: number, dreamId: string) => {
    dispatch({ type: 'DELETE_COACH', payload: coachId })
    await fetchCoaches(dreamId)
  }

  /**
   * 3. 스케줄러용 코치 생성 호출
   * 스케줄러에서 코치를 자동 생성
   * 주의: 이 함수는 dreamId를 받지 않으므로, 호출하는 쪽에서 필요한 dreamId로 새로고침해야 함
   */
  const generateCoach = async () => {
    dispatch({ type: 'SET_LOADING', payload: false })
  }

  /**
   * 코치 선택
   */
  const selectCoach = (coach: DreamityCoachResDto | null) => {
    dispatch({ type: 'SET_SELECTED_COACH', payload: coach })
  }

  return (
    <CoachContext.Provider
      value={{
        ...state,
        fetchCoaches,
        deleteCoach,
        generateCoach,
        selectCoach,
      }}
    >
      {children}
    </CoachContext.Provider>
  )
}

// useCoach hook은 hooks/useCoach.ts에서 export합니다
export function useCoachContext() {
  const context = useContext(CoachContext)
  if (context === undefined) {
    throw new Error('useCoach must be used within a CoachProvider')
  }
  return context
}

